"use client";

import { useState } from "react";
import ActionButton from "@repo/ui/action-button";
import { getNpsThemeClasses } from "@repo/nps/branded";
import Checkbox from "@repo/ui/checkbox";
import { BodyText, HeadlineText } from "@repo/ui/typography";
import { twMerge } from "tailwind-merge";
import type { NpsCopy } from "../types";

interface FollowUpProps {
  copy: NpsCopy;
  score: number;
  onSend: (message: string, canContact: boolean) => void;
  onSkip: () => void;
}

export function FollowUp({ copy, score, onSend, onSkip }: FollowUpProps) {
  const [message, setMessage] = useState("");
  const [canContact, setCanContact] = useState(false);
  const question = copy.followUp(score);
  const themeClasses = getNpsThemeClasses();

  const handleSend = () => {
    onSend(message.trim(), canContact);
  };

  const textareaClass = twMerge(
    "w-full min-h-[120px] p-[16px] rounded-[12px] text-[15px] leading-[22px] tracking-[0.25px] resize-none outline-none focus:outline-none border-2",
    themeClasses.textarea
  );

  return (
    <div className="flex flex-col gap-[24px] items-center w-full">
      {/* Question */}
      <div className="flex flex-col gap-[8px] items-center w-full">
        <HeadlineText
          className={twMerge(
            themeClasses.title,
            "text-center text-[20px] leading-[28px] tracking-[0.2px] w-full"
          )}
        >
          {question}
        </HeadlineText>
      </div>

      {/* Textarea Input */}
      <div className="w-full">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Share your thoughts..."
          className={textareaClass}
          aria-label="Follow-up feedback"
        />
      </div>

      {/* Consent Toggle */}
      <div className="flex items-center gap-[12px] w-full">
        <Checkbox
          id="nps-contact-checkbox"
          checked={canContact}
          onChange={setCanContact}
          aria-label="Allow contact about feedback"
          className="w-[20px] h-[20px] shrink-0"
        />
        <label htmlFor="nps-contact-checkbox" className="flex-1 cursor-pointer">
          <BodyText
            className={twMerge(
              themeClasses.label,
              "text-[15px] leading-[22px] tracking-[0.25px]"
            )}
          >
            {copy.consentLabel}
          </BodyText>
        </label>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-[16px] items-center w-full">
        <ActionButton
          text="Send"
          onPress={handleSend}
          eventData={{
            action: "nps_followup_send",
          }}
          variant="primary"
        />
        <button
          type="button"
          onClick={onSkip}
          className="h-[44px] w-full rounded-[99px] flex items-center justify-center px-[24px] py-[14px]"
        >
          <BodyText className={themeClasses.tertiary}>Skip</BodyText>
        </button>
      </div>
    </div>
  );
}
